/**
 * EXPORT YOUR ORGANIZATION.
 *
 * Section 6 of the subscription agreement is a document customers sign, and it
 * says: "At any time during the term, and for 30 days after it ends, you can
 * export your policies, your document files, and your acknowledgement records
 * through the service." Until this page existed there was no button, so a
 * procurement reviewer who read that clause and went looking found a
 * discrepancy in a signed contract. This is the button.
 *
 * It is the customer's own export of their own organization: the backend takes
 * the organization from the verified session and accepts no organization from
 * the caller at all (`functions/src/privacy/exportAccess.ts`).
 *
 * The archive is built in batches, the way the library import runs: the server
 * does as much as fits in one invocation, writes it as a complete zip, and
 * asks to be called again. This page is what does the asking, which is why it
 * says so plainly — closing it pauses the build rather than losing it.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { useAuth } from '../../auth/useAuth';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import page from '../../shared/Page.module.css';
import admin from './Admin.module.css';
import own from './DataExport.module.css';
import {
  formatBytes,
  formatMoment,
  progressFraction,
  progressLine,
  shouldContinue,
  summaryStatus,
  type ExportJob,
  type ExportSummary,
} from './dataExport';

/** Building a part reads every file it holds; this is not a 30-second call. */
const LONG = { timeoutMs: 540_000 };

/**
 * A ceiling on how many times this page will ask for another batch.
 *
 * Each batch writes up to 100 MB, so this is 20 GB — more than any library we
 * expect and still a number rather than "until something gives". A page that
 * looped forever against a server making no progress would be indistinguishable
 * from working, which is the one thing a progress display must never be.
 */
const MAX_BATCHES = 200;

function errorText(e: unknown, fallback: string): string {
  return isCallFailure(e) ? e.message : fallback;
}

export function DataExportPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [job, setJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const busy = useRef(false);
  const batches = useRef(0);

  const previous = useQuery({
    queryKey: ['dataExports', session?.orgId],
    queryFn: () =>
      call<Record<string, never>, { exports: ExportSummary[]; retentionDays: number }>(
        'listDataExports',
        {},
      ),
    enabled: Boolean(session?.orgId),
    staleTime: 0,
  });

  /**
   * One continuation per render while the job says it is running. `busy` stops
   * a second call going out under React's strict double-invoke, and the batch
   * count stops the loop if the server ever stopped making progress.
   */
  useEffect(() => {
    if (!shouldContinue(job) || !job || busy.current) return;
    busy.current = true;
    let dropped = false;
    void (async () => {
      try {
        if (batches.current >= MAX_BATCHES) {
          setError(
            'This export is taking more parts than expected. What has been built is below. Tell StandingDocs support, and they will finish it.',
          );
          return;
        }
        batches.current += 1;
        const next = await call<{ exportId: string }, ExportJob>(
          'continueDataExport',
          { exportId: job.exportId },
          LONG,
        );
        if (dropped) return;
        setJob(next);
        if (next.status !== 'running') queryClient.invalidateQueries({ queryKey: ['dataExports'] });
      } catch (e) {
        if (!dropped) setError(errorText(e, 'The export stopped.'));
      } finally {
        busy.current = false;
      }
    })();
    return () => {
      dropped = true;
    };
  }, [job, queryClient]);

  const start = async () => {
    setError(null);
    setStarting(true);
    batches.current = 0;
    try {
      const first = await call<Record<string, never>, ExportJob>('requestDataExport', {}, LONG);
      setJob(first);
      queryClient.invalidateQueries({ queryKey: ['dataExports'] });
    } catch (e) {
      setError(errorText(e, 'The export could not be started.'));
    } finally {
      setStarting(false);
    }
  };

  const open = async (exportId: string) => {
    setError(null);
    batches.current = 0;
    try {
      setJob(await call<{ exportId: string }, ExportJob>('getDataExportStatus', { exportId }));
    } catch (e) {
      setError(errorText(e, 'That export could not be opened.'));
    }
  };

  if (!session?.orgId) return null;

  const running = job?.status === 'running';
  const rows = previous.data?.exports ?? [];
  const retentionDays = job?.retentionDays ?? previous.data?.retentionDays ?? 7;

  return (
    <>
      <header className={page.header}>
        <h1 className={page.title}>Export your organization</h1>
        <p className={page.subtitle}>
          Everything you have put into StandingDocs, as an archive you can keep:
          your policies with their details, the document files themselves, and
          your acknowledgement records. Your content is yours, and this is the
          way out — you do not have to ask us for it.
        </p>
      </header>

      <div className={page.body}>
        {error && (
          <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
            {error}
          </div>
        )}

        <section className={own.panel}>
          <h2 className={own.panelTitle}>What an export contains</h2>
          <ul className={own.included}>
            <li>
              <b>Every policy</b>, with its number, title, version, status,
              effective date, next review date, department, category, document
              type and owner — as a spreadsheet you can open in Excel.
            </li>
            <li>
              <b>The document files</b>, filed by department and then by policy.
              The version in force sits at the top of each policy&rsquo;s folder
              and says so in its name; superseded versions are in a folder
              beneath it, so a copy that is no longer current cannot be mistaken
              for one that is.
            </li>
            <li>
              <b>Your acknowledgement records</b> — who confirmed reading which
              version of which policy, and when.
            </li>
            <li>
              <b>Attestations imported from your previous system</b>, in their
              own file. Every row names the system it came from, because those
              are that system&rsquo;s records: StandingDocs did not watch those
              people read those policies and does not claim to.
            </li>
          </ul>
          <p className={own.lead}>
            Anything that could not be put in the archive is listed inside it,
            with the reason. Nothing is left out silently.
          </p>
          <div className={own.actions}>
            <button
              type="button"
              className={`${page.btn} ${page.btnPrimary}`}
              onClick={start}
              disabled={starting || running}
            >
              {starting ? 'Starting…' : running ? 'Building…' : 'Start an export'}
            </button>
            {!running && rows.some((r) => r.status === 'running') && (
              <button
                type="button"
                className={page.btn}
                onClick={() => open(rows.find((r) => r.status === 'running')!.id)}
              >
                Carry on with the export already started
              </button>
            )}
          </div>
        </section>

        {job && (
          <section className={own.panel} aria-labelledby="export-progress">
            <h2 className={own.panelTitle} id="export-progress">
              {job.status === 'complete'
                ? 'Your export is ready'
                : job.status === 'failed'
                  ? 'This export stopped'
                  : 'Building your export'}
            </h2>

            {job.status !== 'failed' && (
              <div className={own.track} aria-hidden="true">
                <div className={own.fill} style={{ width: `${Math.round(progressFraction(job) * 100)}%` }} />
              </div>
            )}

            <p className={own.progressLine} role="status" aria-live="polite">
              {progressLine(job)}
            </p>

            {job.status === 'running' && (
              <p className={own.keepOpen}>
                Keep this page open while it builds. An export is assembled in
                pieces and this page asks for the next one; if you close it, what
                is finished is kept and you can carry on from here later.
              </p>
            )}

            {job.status === 'failed' && (
              <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
                {job.error ?? 'The export stopped.'} Anything already built is
                below, and it is complete as far as it goes. Starting again
                builds a fresh archive.
              </div>
            )}

            {job.status === 'complete' && !job.workbookIncluded && (
              <div className={`${page.notice} ${page.noticeWarn}`} role="status">
                Your library was too large to build a single Excel workbook, so
                the manifest is in the CSV files instead. They hold everything.
              </div>
            )}

            {job.parts.length > 0 && (
              <>
                <ul className={own.parts}>
                  {job.parts.map((part) => (
                    <li className={own.part} key={part.name}>
                      <span className={own.partName}>{part.name}</span>
                      <span className={own.partMeta}>
                        {formatBytes(part.sizeBytes)} · {part.contains}
                      </span>
                      {part.url ? (
                        <a className={page.btn} href={part.url} download={part.name}>
                          Download
                        </a>
                      ) : (
                        <span className={own.partMeta}>
                          This file is no longer available. Start a new export.
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className={own.keepOpen}>
                  Each file is a complete zip on its own; unzip them all into one
                  folder and the folders merge. Download links last a few minutes
                  — use <b>Refresh the links</b> if one has expired. The files
                  themselves are kept for {retentionDays} days.
                </p>
                <div className={own.actions}>
                  <button type="button" className={page.btn} onClick={() => open(job.exportId)}>
                    Refresh the links
                  </button>
                </div>
              </>
            )}

            {job.problems.length > 0 && (
              <div className={admin.scroller} style={{ marginTop: 'var(--sp-4)' }}>
                <table className={admin.table}>
                  <caption>
                    {job.counts.problems} {job.counts.problems === 1 ? 'thing is' : 'things are'} not
                    in this archive. The same list is inside it, as
                    manifest/not-included.csv.
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Where</th>
                      <th scope="col">What</th>
                      <th scope="col">Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.problems.map((p, i) => (
                      <tr key={`${p.where}-${p.what}-${i}`}>
                        <td>{p.where}</td>
                        <td>{p.what}</td>
                        <td>{p.why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        <section className={own.panel}>
          <h2 className={own.panelTitle}>Exports you have made</h2>
          {previous.isLoading ? (
            <Spinner label="Loading previous exports" />
          ) : previous.error ? (
            <PageState title="Could not load your previous exports" tone="alert">
              {previous.error instanceof Error
                ? previous.error.message
                : 'Something went wrong. Reload the page to try again.'}
            </PageState>
          ) : rows.length === 0 ? (
            <p className={own.lead}>
              You have not exported your organization yet. It costs you nothing to
              do it, and you can do it as often as you need.
            </p>
          ) : (
            <div className={admin.scroller}>
              <table className={admin.table}>
                <caption>
                  The record of who exported and when is kept permanently, in your
                  audit log. The files are kept for {retentionDays} days.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Started</th>
                    <th scope="col">By</th>
                    <th scope="col">Policies</th>
                    <th scope="col">Documents</th>
                    <th scope="col">State</th>
                    <th scope="col">
                      <span className="vh">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatMoment(row.requestedAtIso)}</td>
                      <td>{row.requestedByName || row.requestedByEmail || '—'}</td>
                      <td className="tnum">{row.counts?.policies ?? '—'}</td>
                      <td className="tnum">{row.counts?.files ?? '—'}</td>
                      <td>{summaryStatus(row)}</td>
                      <td>
                        {row.purged ? (
                          <span className={own.partMeta}>Files removed</span>
                        ) : (
                          <button type="button" className={page.linkBtn} onClick={() => open(row.id)}>
                            Open
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
